import { RacunPoEposti } from './RacunPoEposti';
import {
  Alert,
  Anchor,
  Button,
  Group,
  List,
  Menu,
  Modal,
  NumberInput,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconChevronDown,
  IconFileCode,
  IconMail,
  IconPencil,
  IconPrinter,
  IconReceiptOff,
  IconSend,
  IconShieldCheck,
} from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { odpriPdf, prenesiDatoteko } from '../api/pdf';
import { datum, denar } from '../oblika/oblika';
import { imeOsebe } from '../osebe/vrste';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { TabelaArtiklov, TabelaStoritev } from '../postavitev/Postavke';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { Oznaka } from './PregledRacunov';
import { usePravica } from '../seja/pravice';

type Racun = Shema['RacunPodrobnosti'];

const pdfRacuna = (id: number) => api.GET('/api/racuni/{id}/pdf', { params: { path: { id } }, parseAs: 'blob' });

/**
 * Podrobnosti izdanega računa: podatki, storitve, artikli in dejanja »Račun« (PDF, poveča
 * števec tiskanj), garancija (doda jo, če je ni), prevzemni zapisnik, storno in dobropis (stornirajoči račun izda
 * strežnik in ga takoj odpre), izvoz eSLOG XML (s strežnika) in urejanje (račun, ki se ne pošilja na FURS in ni
 * storniran).
 */
export function PodrobnostiRacuna() {
  const { id } = useParams();
  const poizvedba = useQuery({
    queryKey: ['racun', Number(id)],
    queryFn: ({ signal }) => podatki(api.GET('/api/racuni/{id}', { signal, params: { path: { id: Number(id) } } })),
  });
  if (!poizvedba.data) {
    return (
      <StanjeNalaganja
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        naslov="Računa ni bilo mogoče naložiti"
      />
    );
  }
  return <Racun r={poizvedba.data} />;
}

function Racun({ r }: { r: Racun }) {
  const odjemalec = useQueryClient();
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<string | null>(null);
  const [storno, setStorno] = useState<'storno' | 'dobropis' | null>(null);
  const [garancija, setGarancija] = useState(false);
  const stornoRacuna = !!r.storniranaStevilka;
  const zakljucen = r.storniran || stornoRacuna;
  // računov za FURS in storniranih ni mogoče urejati
  const urejanje = !zakljucen && !r.posljiNaFurs;
  const urejanjeRacunov = usePravica('RACUNI', 'UREJANJE');

  const osvezi = async () => {
    await odjemalec.invalidateQueries({ queryKey: ['racun', r.id] });
    await odjemalec.invalidateQueries({ queryKey: ['racuni'] });
  };

  /** Izvede dejanje z oznako na gumbu in napako nad podatki. */
  const izvedba = async (kdo: string, dejanje: () => Promise<void>) => {
    setNapaka(null);
    setDela(kdo);
    try {
      await dejanje();
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(null);
    }
  };

  // PDF, nato števec tiskanj (naslednji izpis je »Kopija«)
  const natisni = () =>
    izvedba('racun', async () => {
      await odpriPdf(pdfRacuna(r.id!));
      await izvedi(api.POST('/api/racuni/po-stevilki/natisnjen', { params: { query: { stevilka: r.stRacuna! } } }));
      await osvezi();
    });

  const garancijaPdf = () =>
    izvedba('garancija', () =>
      odpriPdf(api.GET('/api/racuni/{id}/garancija/pdf', { params: { path: { id: r.id! } }, parseAs: 'blob' })),
    );

  const prevzemni = (voziloId: number) =>
    izvedba('prevzemni', () =>
      odpriPdf(
        api.GET('/api/racuni/{id}/prevzemni/pdf', {
          params: { path: { id: r.id! }, query: { vozilo: voziloId } },
          parseAs: 'blob',
        }),
      ),
    );

  // eSLOG izdela in preveri strežnik; neveljaven račun vrne razloge (409)
  const eslog = () =>
    izvedba('eslog', () =>
      prenesiDatoteko(
        api.GET('/api/racuni/{id}/eslog', { params: { path: { id: r.id! } }, parseAs: 'blob' }),
        `${r.stRacuna}_eslog.xml`,
      ),
    );

  const posljiFurs = () =>
    izvedba('furs', async () => {
      const o = await podatki(api.POST('/api/racuni/{id}/furs', { params: { path: { id: r.id! } } }));
      await osvezi();
      if (o.napaka) {
        throw new NapakaApi(`Račun ni bil davčno potrjen: ${o.napaka}`, 'FURS', 0);
      }
      notifications.show({ color: 'green', message: 'Račun je davčno potrjen.' });
    });

  const vozila = r.vozila ?? [];
  const [eposta, setEposta] = useState(false);
  const dejanja = (
    <Group gap="xs" wrap="wrap">
      <Button leftSection={<IconPrinter size={18} />} onClick={() => void natisni()} loading={dela === 'racun'}>
        Račun
      </Button>
      {urejanje && urejanjeRacunov && (
        <Button variant="default" leftSection={<IconPencil size={18} />} component={Link} to={`/racuni/${r.id}/uredi`}>
          Uredi
        </Button>
      )}
      {!r.storniran && (
        <Button variant="default" leftSection={<IconMail size={18} />} onClick={() => setEposta(true)}>
          Pošlji po e-pošti
        </Button>
      )}
      <Button
        variant="default"
        leftSection={<IconFileCode size={18} />}
        onClick={() => void eslog()}
        loading={dela === 'eslog'}
      >
        Izvozi XML
      </Button>
      {!r.storniran &&
        (r.garancija ? (
          <Button variant="default" onClick={() => void garancijaPdf()} loading={dela === 'garancija'}>
            Garancija
          </Button>
        ) : (
          <Button variant="default" onClick={() => setGarancija(true)}>
            Dodaj garancijo
          </Button>
        ))}
      {!zakljucen &&
        vozila.length > 0 &&
        (vozila.length === 1 ? (
          <Button variant="default" onClick={() => void prevzemni(vozila[0]!.voziloId!)} loading={dela === 'prevzemni'}>
            Prevzemni zapisnik
          </Button>
        ) : (
          <Menu position="bottom-start">
            <Menu.Target>
              <Button variant="default" rightSection={<IconChevronDown size={16} />} loading={dela === 'prevzemni'}>
                Prevzemni zapisnik
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {vozila.map((v) => (
                <Menu.Item key={v.id} onClick={() => void prevzemni(v.voziloId!)}>
                  {[v.znamka, v.tip, v.vin].filter(Boolean).join(' · ')}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        ))}
      {!zakljucen && (
        <>
          <Button variant="default" onClick={() => setStorno('dobropis')}>
            Dobropis
          </Button>
          <Button
            variant="light"
            color="red"
            leftSection={<IconReceiptOff size={18} />}
            onClick={() => setStorno('storno')}
          >
            Storniraj
          </Button>
        </>
      )}
    </Group>
  );

  return (
    <ZaslonSeznama>
      <Button component={Link} to="/racuni" variant="subtle" leftSection={<IconArrowLeft size={16} />} mb="xs" px={0}>
        Računi
      </Button>
      <GlavaZaslona naslov={`Račun ${r.stRacuna ?? ''}`} opis={imeOsebe(r.kupec)} dejanja={dejanja} />
      <Stack gap="sm" mb="md">
        {napaka && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} role="alert">
            {napaka}
          </Alert>
        )}
        {r.storniran && (
          <Alert color="red" variant="light" icon={<IconReceiptOff size={18} />}>
            Račun je storniran.
          </Alert>
        )}
        {stornoRacuna && (
          <Alert color="orange" variant="light" icon={<IconReceiptOff size={18} />}>
            {r.dobropis ? 'Dobropis' : 'Storno'} računa{' '}
            <Anchor component={Link} to={`/racuni?q=${encodeURIComponent(r.storniranaStevilka!)}`} fw={600}>
              {r.storniranaStevilka}
            </Anchor>
            .
          </Alert>
        )}
        {r.posljiNaFurs && !r.eor && (
          <Alert color="yellow" variant="light" icon={<IconAlertCircle size={18} />} title="Račun še ni davčno potrjen">
            <Text size="sm">Pošlje se samodejno ob naslednji prijavi v Hero ali zdaj:</Text>
            <Button
              size="xs"
              mt="xs"
              variant="light"
              color="yellow"
              leftSection={<IconSend size={14} />}
              onClick={() => void posljiFurs()}
              loading={dela === 'furs'}
            >
              Pošlji na FURS
            </Button>
          </Alert>
        )}
      </Stack>
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <Kartica naslov="Podatki">
            <Podatki
              vrstice={[
                ['Številka', <Stevilka key="s" r={r} />],
                ['Začetek storitve', datum(r.datumOpravljeneStoritveZacetek)],
                ['Konec storitve', datum(r.datumOpravljeneStoritveKonec)],
                ['Ustvarjen', datum(r.datum)],
                ['Potek', datum(r.datumPlacila)],
                ['Znesek artiklov', denar(r.znesekArtiklov)],
                ['Znesek storitev', denar(r.znesekStoritev)],
                ['Skupaj', <b key="z">{denar(r.znesek)}</b>],
                ['Kupec', <Oseba key="k" vrsta="kupci" o={r.kupec} />],
                ['Uporabnik', <Oseba key="u" vrsta="uporabniki-vozil" o={r.uporabnikVozila} />],
                ['Ref. za plačilo', r.referencaZaPlacilo],
                ['Koda namena', r.paymentClassification || 'GDSV'],
              ]}
            />
          </Kartica>
          <Stack gap="md">
            <Kartica naslov="Storitve">
              <TabelaStoritev storitve={r.storitve ?? []} />
            </Kartica>
            <Kartica naslov="Davčno potrjevanje in garancija">
              <Podatki
                vrstice={[
                  ['Gotovinsko plačilo', r.posljiNaFurs ? 'Da (FURS)' : 'Ne'],
                  ['ZOI', <Dolgo key="zoi">{r.zoi}</Dolgo>],
                  ['EOR', <Dolgo key="eor">{r.eor}</Dolgo>],
                  ['Prostor / naprava', [r.poslovniProstor, r.elektronskaNaprava].filter(Boolean).join(' / ')],
                  [
                    'Garancija',
                    r.garancija
                      ? `${r.garancija.stGarancije ?? ''} · ${r.garancija.trajanje ?? 0} mes. · velja do ${datum(r.garancija.veljaDo)}`
                      : '',
                  ],
                  ['Natisnjen', `${r.stPrintanj ?? 0}×`],
                ]}
              />
            </Kartica>
          </Stack>
        </SimpleGrid>
        <Kartica naslov="Artikli">
          <TabelaArtiklov vozila={vozila} />
        </Kartica>
        {(r.opombe || (r.klavzule ?? []).length > 0) && (
          <Kartica naslov="Opombe in klavzule">
            {r.opombe && (
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} mb={(r.klavzule ?? []).length > 0 ? 'sm' : 0}>
                {r.opombe}
              </Text>
            )}
            <List size="sm" spacing={4}>
              {(r.klavzule ?? []).map((k) => (
                <List.Item key={k.id}>{k.besedilo}</List.Item>
              ))}
            </List>
          </Kartica>
        )}
      </Stack>
      <StornoOkno racun={r} vrsta={storno} zapri={() => setStorno(null)} />
      <GarancijaOkno
        racun={r}
        odprto={garancija}
        zapri={() => setGarancija(false)}
        dodana={async () => {
          setGarancija(false);
          await osvezi();
        }}
      />
      <RacunPoEposti racunId={r.id!} odprto={eposta} zapri={() => setEposta(false)} />
    </ZaslonSeznama>
  );
}

function Stevilka({ r }: { r: Racun }) {
  return (
    <Group gap={6} component="span">
      <span>{r.stRacuna}</span>
      {r.storniran && <Oznaka barva="red">storniran</Oznaka>}
      {r.dobropis && <Oznaka barva="orange">dobropis</Oznaka>}
      {!r.dobropis && r.storniranaStevilka && <Oznaka barva="orange">storno</Oznaka>}
    </Group>
  );
}

function Oseba({ vrsta, o }: { vrsta: string; o?: Shema['OsebaPodatki'] }) {
  if (!o?.id) {
    return null;
  }
  return (
    <Anchor component={Link} to={`/osebe/${vrsta}/${o.id}`} size="sm" fw={500}>
      {imeOsebe(o) || `#${o.id}`}
    </Anchor>
  );
}

/** Dolga koda (ZOI, EOR): na telefonu se prelomi kjerkoli. */
function Dolgo({ children }: { children?: ReactNode }) {
  return <span style={{ wordBreak: 'break-all' }}>{children}</span>;
}

/**
 * Storno ali dobropis: neobvezna opomba, nato strežnik izda stornirajoči račun, ga za
 * FURS davčno potrdi, tu pa se odpre njegov PDF (in poveča števec tiskanj) in prikaže nov račun.
 */
function StornoOkno({ racun, vrsta, zapri }: { racun: Racun; vrsta: 'storno' | 'dobropis' | null; zapri: () => void }) {
  const navigate = useNavigate();
  const odjemalec = useQueryClient();
  const [opomba, setOpomba] = useState('');
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const dobropis = vrsta === 'dobropis';

  const potrdi = async () => {
    setNapaka(null);
    setDela(true);
    // spremenljivka v objektu: TypeScript ne sledi prirejanju v funkciji spodaj
    const izid: { nov?: Racun } = {};
    try {
      // zavihek za PDF se odpre ob kliku (sicer ga brskalnik blokira), storno in PDF nato v njem
      await odpriPdf(
        (async () => {
          izid.nov = await podatki(
            api.POST('/api/racuni/{id}/storno', {
              params: { path: { id: racun.id! } },
              body: { dobropis, opomba: opomba.trim() || undefined },
            }),
          );
          return pdfRacuna(izid.nov.id!);
        })(),
      );
    } catch (e) {
      if (!izid.nov) {
        setNapaka(sporocilo(e));
        setDela(false);
        return;
      }
      notifications.show({ color: 'red', message: `PDF računa ${izid.nov.stRacuna} ni bil odprt: ${sporocilo(e)}` });
    }
    const nov = izid.nov;
    if (!nov) {
      setDela(false);
      return;
    }
    try {
      await izvedi(api.POST('/api/racuni/po-stevilki/natisnjen', { params: { query: { stevilka: nov.stRacuna! } } }));
    } catch {
      // števec tiskanj ni bistven; račun je izdan
    }
    await odjemalec.invalidateQueries({ queryKey: ['racuni'] });
    await odjemalec.invalidateQueries({ queryKey: ['racun'] });
    await odjemalec.invalidateQueries({ queryKey: ['vozilo'] });
    notifications.show({
      color: 'green',
      message: `${dobropis ? 'Dobropis' : 'Storno'} ${nov.stRacuna} je izdan, račun ${racun.stRacuna} je storniran.`,
    });
    if (nov.posljiNaFurs && !nov.eor) {
      notifications.show({
        color: 'yellow',
        autoClose: false,
        message: `Račun ${nov.stRacuna} ni bil davčno potrjen. Poslan bo ob naslednji prijavi.`,
      });
    }
    setDela(false);
    setOpomba('');
    zapri();
    void navigate(`/racuni/${nov.id}`);
  };

  return (
    <Modal
      opened={vrsta !== null}
      onClose={() => !dela && zapri()}
      title={dobropis ? `Dobropis k računu ${racun.stRacuna}` : `Storniraj račun ${racun.stRacuna}`}
      radius="lg"
    >
      <Stack gap="sm">
        <Text size="sm">
          Izdan bo nov račun z negativnimi zneski vseh postavk, račun {racun.stRacuna} bo označen kot storniran, vozila
          z računa pa se vrnejo na zalogo. Tega ni mogoče razveljaviti.
        </Text>
        <Textarea
          label="Opomba (neobvezno)"
          value={opomba}
          onChange={(e) => setOpomba(e.currentTarget.value)}
          autosize
          minRows={2}
          maxRows={6}
          data-autofocus
        />
        {napaka && (
          <Alert color="red" variant="light" role="alert">
            {napaka}
          </Alert>
        )}
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={zapri} disabled={dela}>
            Prekliči
          </Button>
          <Button color="red" onClick={() => void potrdi()} loading={dela}>
            {dobropis ? 'Izdaj dobropis' : 'Storniraj'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Račun brez garancije: število mesecev, strežnik doda garancijo, nato se odpre garancijski list. */
function GarancijaOkno({
  racun,
  odprto,
  zapri,
  dodana,
}: {
  racun: Racun;
  odprto: boolean;
  zapri: () => void;
  dodana: () => Promise<void>;
}) {
  const [meseci, setMeseci] = useState<number | string>(12);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);

  const potrdi = async () => {
    setNapaka(null);
    setDela(true);
    const izid = { dodano: false };
    try {
      await odpriPdf(
        (async () => {
          await podatki(
            api.POST('/api/racuni/{id}/garancija', {
              params: { path: { id: racun.id! } },
              body: { vrednost: Number(meseci) || 0 },
            }),
          );
          izid.dodano = true;
          return api.GET('/api/racuni/{id}/garancija/pdf', { params: { path: { id: racun.id! } }, parseAs: 'blob' });
        })(),
      );
    } catch (e) {
      if (!izid.dodano) {
        setNapaka(sporocilo(e));
        setDela(false);
        return;
      }
      notifications.show({ color: 'red', message: `Garancija je dodana, PDF pa ni bil odprt: ${sporocilo(e)}` });
    }
    setDela(false);
    notifications.show({ color: 'green', message: 'Garancija je dodana.' });
    await dodana();
  };

  return (
    <Modal opened={odprto} onClose={() => !dela && zapri()} title="Dodaj garancijo" radius="lg">
      <Stack gap="sm">
        <NumberInput
          label="Trajanje garancije (meseci)"
          value={meseci}
          onChange={setMeseci}
          min={1}
          max={120}
          allowDecimal={false}
          leftSection={<IconShieldCheck size={16} />}
          error={napaka}
          data-autofocus
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={zapri} disabled={dela}>
            Prekliči
          </Button>
          <Button onClick={() => void potrdi()} loading={dela}>
            Dodaj in natisni
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
